/**
 * **The opposed-check primitive** — the engine's **two-roll contested check**. Most
 * resolutions are one-roll-vs-a-number (`to_hit`: d20+bonus vs a defense; `save`:
 * d20+bonus vs a DC). RAW also has "opposed" checks — Search (`1d20 + systems` vs
 * `1d20 + agility`), Grapple's break/tiebreak (`1d20 + HULL` vs `1d20 + HULL`), Drag
 * Down, and (as a reaction) Stasis Bolt's Interdiction Point — where **both** sides
 * roll and the higher total wins. `resolveContest` is that shape, drawn once here so
 * every consumer shares it. Confirmed against the full `lancer-data` content set: no
 * contest is spliced mid-effect-chain, so this is a standalone rules-layer function,
 * NOT a DSL/effect-chain atom.
 *
 * Like {@link resolveSaveEnds} (`saves.ts`, its sibling in this directory),
 * `resolveContest` is a **draw-and-record** resolution: it sources both d20s through
 * the injected {@link DiceSource} and records one **record-only** `ContestStep`
 * (`@lancer-vtt/domain`, inverts to `null` — like `to_hit`/`save`). Unlike
 * `resolveSaveEnds` it does **not** build a full `apply_resolved` command itself: a
 * contest is always wrapped by a **consumer** (the `request_search` intent, Grapple/Drag
 * Down) that appends its own grounded consequence steps (a Search reveal, a Grapple
 * break) alongside the returned `step` — **consequences attach to the loser, who may be
 * the initiator** (Drag Down: "the loser is knocked Prone"). So `resolveContest` returns
 * plain outcome data — no `choose`/`if` control atom lives inside it.
 *
 * **Each side's contribution is a consumer-resolved number + an axis label**, not a
 * `base[axis]` read — the content proves the "axis" isn't always a HASE field (Stasis
 * Bolt contests a *ranged attack roll* = d20 + attack bonus). The consumer resolves
 * `challengerBonus`/`defenderBonus` itself (Search reads `base.systems`/`base.agility`)
 * and passes the axis name through only as a label for the record.
 *
 * **Modifier folding:** a contested check IS a check, so **Bolster** ("+2◆ on the next
 * skill check or save") must apply to a Search exactly as it applies to a `save_ends`
 * re-save — `resolveContest` folds each side's own overlay accuracy/difficulty modifiers
 * into its roll via the **same seam** `resolveSaveEnds` uses
 * (`foldModifiers`/`seedModifiers`, the carrier-self
 * `saveCheckBonus`/`consumeOnSaveCheck` dimension), and consumes the boon on use exactly
 * like the save re-roll does. The auto-win/"can't-fail" case (Armor-Lock Plating) plugs
 * into this same fold later — deferred, its content isn't authored yet.
 *
 * **Tie rule: the initiator (challenger) wins — one RAW rule, no per-consumer
 * parameter.** SRD `basics.json`: "If the result is a tie,
 * the attacker — the one who initiated the contest — wins." The pure comparison
 * ({@link compareContestRolls}) is factored out, decoupled from the dice/state/intent
 * plumbing, so a future reaction consumer (Stasis Bolt's Interdiction Point) can
 * compare two already-rolled totals directly without going through `resolveContest`
 * at all.
 */
import type { CombatantId, ContestStep, EncounterState, ResolvedStep } from "@lancer-vtt/domain";
import { rollD20, type DiceSource } from "../dice";
import { SEED_OVERLAY } from "./effects/seed";
import type { LoadedOverlay } from "./effects/loader";
import { foldSelfSaveCheck, spendConsumedStatuses, spendFoldedCaps } from "./selfCheck";

/** Which side of a contest a combatant occupies. */
export type ContestSide = "challenger" | "defender";

/** The pure "draw two rolls → winner/tie" comparison's result. */
export type ContestComparison = {
  winner: ContestSide;
  /** `true` iff the totals are equal (in which case `winner` is always `"challenger"`). */
  tie: boolean;
};

/**
 * The pure "draw two rolls → winner/loser/tie" comparison,
 * decoupled from the intent plumbing: it takes two already-computed totals and returns
 * the RAW outcome, nothing else. This is the seam a future reaction consumer (Stasis
 * Bolt's Interdiction Point: a contested *ranged attack roll*) can call directly,
 * without routing through `resolveContest`'s dice/state/overlay machinery.
 *
 * Per RAW (SRD `basics.json`, un-errata'd): "If the result is a tie, the attacker — the
 * one who initiated the contest — wins." The challenger is always the initiator, so a
 * tie resolves as a challenger win.
 */
export function compareContestRolls(challengerTotal: number, defenderTotal: number): ContestComparison {
  if (challengerTotal >= defenderTotal) {
    return { winner: "challenger", tie: challengerTotal === defenderTotal };
  }
  return { winner: "defender", tie: false };
}

/** One side's rolled contribution to a contest, plus any modifier-consumption this draw produced. */
type SideDraw = {
  roll: ContestStep["challengerRoll"];
  /**
   * `true` iff this side auto-failed (Stunned, and the axis names a stat it auto-fails)
   * — no die drawn, `roll.d20` reads `0`. The caller
   * forces this side to lose the contest outright rather than comparing totals.
   */
  autoFailed: boolean;
  /** Steps this side's modifier fold produced (a Bolster `status_remove` on consume). */
  consumeSteps: ResolvedStep[];
  warnings: string[];
};

/** Named args for {@link rollSide} (`CODE_STYLE.md` max-params-3). */
type RollSideArgs = {
  state: EncounterState;
  dice: DiceSource;
  id: CombatantId;
  bonus: number;
  overlay: LoadedOverlay;
  /** The axis label (e.g. `"hull"`) — checked against Stunned's `autoFailChecks`. */
  axis: string;
};

/**
 * Roll one side of a contest: `d20 + bonus (+ folded accuracy)`. Folds the carrier's
 * own overlay modifiers on the carrier-self save/check dimension (Bolster) — the same
 * seam/pattern `resolveSaveEnds`'s `rollSave` uses — and, if a boon applied, emits the
 * consuming `status_remove` step(s) (spent regardless of win/lose, exactly like a
 * `save_ends` re-save spends Bolster regardless of pass/fail).
 *
 * **Auto-fail:** if this side's carrier auto-fails checks/saves
 * naming `axis` (Stunned, RAW Core p.78), no die is drawn — a recorded skip-roll, `d20`
 * reads `0`, `autoFailed: true` — and `resolveContest` forces this side to lose
 * regardless of the other side's total.
 */
function rollSide(args: RollSideArgs): SideDraw {
  const { state, dice, id, bonus, overlay, axis } = args;
  const c = state.combatants[id];
  // Four modifier sources fold in here, all the same carrier-self save/check dimension
  // `resolveSaveEnds` honors: the carrier's statuses, its frozen PILOT-effect
  // (talent/core-bonus) modifiers, its frozen equipped-SYSTEM modifiers (which also carry
  // the `autoFailChecks` trait read below), and a CONFERRED gather (see below).
  //
  // Gathered mods are STAT-scoped via `scopeSaveModifiers`, keyed on THIS side's own
  // `axis` — unlike `saves.ts`'s `save_ends` re-save, a contest's axis is well-defined
  // (the consumer always resolves one, e.g. Search's `"systems"`/`"agility"`) so the
  // matching branch is reachable in production here, not just at the test-argument level.
  // Deliberately no `cause` is threaded — that dimension stays a recorded limitation:
  // "what is the cause of an opposed check?" is an undefined design question, and no
  // PILOT-borne (talent/core-bonus) modifier authors a `saveScope.cause` (grepped the
  // pack: zero hits), so a cause-scoped PILOT modifier can never reach this fold.
  // `ms_stable_structure`'s own `saveScope.cause` facet DOES reach this fold via
  // `seedSystemModifiers` below — but with no `cause` threaded through
  // `scopeSaveModifiers`'s `context`, it strips exactly as conservatively as an unproven
  // dimension always does.
  const mods = foldSelfSaveCheck({ state, carrierId: id, overlay, scope: { stat: axis, roll: "check" } });
  if (mods.autoFailChecks.includes(axis)) {
    return { roll: { d20: 0, accuracy: 0, bonus, total: bonus }, autoFailed: true, consumeSteps: [], warnings: [] };
  }
  const acc = mods.saveCheckBonus;
  const d = rollD20(dice, acc);
  const d20 = d.draws[0]!.value;
  const accuracy = d.accuracyMod ?? 0;
  const total = d20 + bonus + accuracy;

  const consumeSteps: ResolvedStep[] = spendFoldedCaps(mods, undefined);
  const warnings: string[] = [];
  if (acc !== 0) {
    consumeSteps.push(...spendConsumedStatuses({ carrier: c ?? { id, statuses: [] }, statuses: mods.consumeOnSaveCheck, state }));
    for (const status of mods.consumeOnSaveCheck) {
      warnings.push(`${c?.name ?? id}: ${status} grants +${acc}◆ to this contest check, then is consumed.`);
    }
  }

  return { roll: { d20, accuracy, bonus, total }, autoFailed: false, consumeSteps, warnings };
}

export type ResolveContestArgs = {
  state: EncounterState;
  /** The initiator — the combatant who called the contest. Wins ties. */
  challenger: CombatantId;
  /** The target of the contest. */
  defender: CombatantId;
  /** The challenger's flat bonus (consumer-resolved — e.g. `base.systems` for Search). */
  challengerBonus: number;
  /** The defender's flat bonus (consumer-resolved — e.g. `base.agility` for Search). */
  defenderBonus: number;
  /** Label only, for the record/play-by-play (e.g. `"systems"`) — not a `base[axis]` read. */
  challengerAxis: string;
  /** Label only, for the record/play-by-play (e.g. `"agility"`) — not a `base[axis]` read. */
  defenderAxis: string;
  dice: DiceSource;
  /** The overlay to fold each side's own accuracy/difficulty modifiers (Bolster) from; defaults to the seed overlay (what the room uses). */
  overlay?: LoadedOverlay;
};

/** The result of resolving a two-roll opposed/contested check. */
export type ContestOutcome = {
  /** The recorded `ContestStep` (both draws + the outcome) — record-only, self-witnessing. */
  step: ContestStep;
  /**
   * Every step this draw produced, in order: the `ContestStep` first, then any
   * modifier-consumption steps (a Bolstered side's spent `status_remove`). The
   * consumer folds these into its own `apply_resolved` command alongside its
   * gate-specific consequence steps (e.g. Search's `clearHiddenSteps`).
   */
  steps: ResolvedStep[];
  winner: ContestSide;
  tie: boolean;
  /** The winning combatant's id (convenience — `challenger` or `defender`, per `winner`). */
  winnerId: CombatantId;
  /** The losing combatant's id — consequences attach here (the loser may be the initiator). */
  loserId: CombatantId;
  /** Advisory play-by-play (each side's roll, any modifier boon consumed). */
  warnings: string[];
};

/**
 * Resolve a **two-roll opposed/contested check**: both sides roll `1d20 + bonus`
 * (folding each side's own overlay accuracy/difficulty modifiers), the higher total
 * wins, and **the challenger (initiator) wins ties** (RAW).
 * Draw-and-record: both rolls are always recorded on the returned `ContestStep`, win
 * or lose. Returns plain outcome data — no control atom, no command; the caller
 * (Search, later Grapple) wraps `steps` with its own consequence steps into its own
 * `apply_resolved` command.
 */
export function resolveContest(args: ResolveContestArgs): ContestOutcome {
  const {
    state,
    challenger,
    defender,
    challengerBonus,
    defenderBonus,
    challengerAxis,
    defenderAxis,
    dice,
    overlay = SEED_OVERLAY,
  } = args;

  const challengerDraw = rollSide({ state, dice, id: challenger, bonus: challengerBonus, overlay, axis: challengerAxis });
  const defenderDraw = rollSide({ state, dice, id: defender, bonus: defenderBonus, overlay, axis: defenderAxis });

  // A side that auto-failed loses outright, regardless
  // of totals (Stunned's recorded `bonus`-only total could otherwise accidentally beat a
  // low roll). Only when BOTH sides auto-fail does the ordinary tie-goes-to-the-
  // challenger comparison decide (RAW doesn't cover a doubly-auto-failed contest).
  let winner: ContestSide;
  let tie: boolean;
  if (challengerDraw.autoFailed && !defenderDraw.autoFailed) {
    winner = "defender";
    tie = false;
  } else if (defenderDraw.autoFailed && !challengerDraw.autoFailed) {
    winner = "challenger";
    tie = false;
  } else {
    ({ winner, tie } = compareContestRolls(challengerDraw.roll.total, defenderDraw.roll.total));
  }

  const step: ContestStep = {
    op: "contest",
    challenger,
    defender,
    challengerAxis,
    defenderAxis,
    challengerRoll: challengerDraw.roll,
    defenderRoll: defenderDraw.roll,
    winner,
    tie,
    ...(challengerDraw.autoFailed ? { challengerAutoFailed: true } : {}),
    ...(defenderDraw.autoFailed ? { defenderAutoFailed: true } : {}),
  };

  const winnerId = winner === "challenger" ? challenger : defender;
  const loserId = winner === "challenger" ? defender : challenger;

  const steps: ResolvedStep[] = [step, ...challengerDraw.consumeSteps, ...defenderDraw.consumeSteps];
  const warnings = [...challengerDraw.warnings, ...defenderDraw.warnings];

  return { step, steps, winner, tie, winnerId, loserId, warnings };
}
